import {PayloadAction, createEntityAdapter, createSlice} from '@reduxjs/toolkit';
import {makeLayoutQueryKey} from 'src/modules/profile/user/tabs/content/layouts/util';
import {
    getUserLayoutsFailure,
    getUserLayoutsRequest,
    getUserLayoutsSuccess,
} from 'src/openapi/admin/apis/UserLayoutsApi';
import {SortDirection} from 'src/openapi/admin/models/SortDirection';
import {UserLayout} from 'src/openapi/admin/models/UserLayout';
import {UserLayoutRequest, UserLayoutRequestSortBy} from 'src/openapi/admin/models/UserLayoutRequest';

const layoutsAdapter = createEntityAdapter<UserLayout, string>({
    selectId: layout => layout.id,
});

type QueryState = {
    ids: string[],
    total: number,
    status: 'idle' | 'loading' | 'succeeded' | 'failed',
    error?: string,
    receivedAt?: number,
};

export type LayoutsPageState = {
    userPage: {
        tabs: {
            content: {
                layouts: LayoutsSliceState,
            },
        },
    },
};

type LayoutsSliceState = {
    queries: Record<string, QueryState>,
    criteriaByScope: Record<string, UserLayoutRequest>,
} & ReturnType<typeof layoutsAdapter.getInitialState>;

const initialState: LayoutsSliceState = {
    ...layoutsAdapter.getInitialState(),
    queries: {},
    criteriaByScope: {},
};

export const defaultLayoutCriteria: UserLayoutRequest = {
    page: {currentPage: 1, pageSize: 15},
    sortBy: UserLayoutRequestSortBy.NAME,
    sortDirection: SortDirection.ASC,
};

export const layoutsSlice = createSlice({
    name: 'layouts',
    initialState,
    reducers: {
        setScopeCriteria: (
            state,
            action: PayloadAction<{scopeKey: string, patch: Partial<UserLayoutRequest>}>,
        ) => {
            const {scopeKey, patch} = action.payload;
            const prev = state.criteriaByScope[scopeKey] || defaultLayoutCriteria;

            const resetPageNeeded =
                patch.searchByName !== undefined ||
                patch.layoutType !== undefined ||
                patch.entityTypes !== undefined;

            state.criteriaByScope[scopeKey] = {
                ...prev,
                ...patch,
                page: {
                    ...prev.page,
                    ...(patch.page || {}),
                    currentPage: resetPageNeeded
                        ? 1
                        : (patch.page?.currentPage ?? prev.page?.currentPage ?? 1),
                },
            };
        },
        resetScopeCriteria: (state, action: PayloadAction<string>) => {
            state.criteriaByScope[action.payload] = defaultLayoutCriteria;
        },
        invalidateQuery: (state, action: PayloadAction<string>) => {
            const query = state.queries[action.payload];
            if (query) {
                query.receivedAt = undefined;
            }
        },
        invalidateQueriesForUser: (state, action: PayloadAction<number>) => {
            const prefix = `${action.payload}::`;
            Object.keys(state.queries).forEach(key => {
                if (key.startsWith(prefix)) {
                    state.queries[key].status = 'idle';
                    state.queries[key].receivedAt = undefined;
                }
            });
        },
    },
    extraReducers: builder => {
        builder
            .addCase(getUserLayoutsRequest, (state, action) => {
                const {userId, userLayoutRequest} = action.payload.meta;
                if (!userLayoutRequest) {
                    return;
                }
                const key = makeLayoutQueryKey(userId, userLayoutRequest);
                state.queries[key] = {
                    ids: [],
                    total: 0,
                    status: 'loading',
                };
            })
            .addCase(getUserLayoutsSuccess, (state, action) => {
                const {userId, userLayoutRequest} = action.payload.meta;
                if (!userLayoutRequest) {
                    return;
                }
                const {layouts, totalCount} = action.payload.data;
                const key = makeLayoutQueryKey(userId, userLayoutRequest);

                layoutsAdapter.upsertMany(state, layouts);

                state.queries[key] = {
                    ids: layouts.map(l => l.id),
                    total: totalCount,
                    status: 'succeeded',
                    receivedAt: Date.now(),
                };
            })
            .addCase(getUserLayoutsFailure, (state, action) => {
                const {userId, userLayoutRequest} = action.payload.meta;
                if (!userLayoutRequest) {
                    return;
                }
                const key = makeLayoutQueryKey(userId, userLayoutRequest);

                state.queries[key] = {
                    ids: [],
                    total: 0,
                    status: 'failed',
                    error: action.payload.data?.message ?? 'Unknown error',
                };
            });
    },
});

export const {
    setScopeCriteria,
    resetScopeCriteria,
    invalidateQuery,
    invalidateQueriesForUser,
} = layoutsSlice.actions;

export const layoutsSelectors = layoutsAdapter.getSelectors<{
    userPage: {tabs: {content: {layouts: LayoutsSliceState}}},
}>(state => state.userPage.tabs.content.layouts);
